/* eslint-disable react/prop-types */
import React, { useCallback, useMemo } from 'react'
import IntegrationManager from '../../engage/IntegrationManager'
import ManageLicensesModal from '../ManageLicensesModal'
import OffboardModal from '../../engage/modals/OffboardModal'
import ReRegisterMfaModal from '../ReRegisterMfaModal'
import TemporaryAccessPassModal from '../../engage/modals/TemporaryAccessPass'
import OutOfOfficeModal from '../../engage/modals/OutOfOffice'
import GlobalAddressListModal from '../../engage/modals/GlobalAddressList/GlobalAddressListModal'
import ManageEmailForwarding from '../../engage/modals/ManageEmailForwarding/ManageEmailForwarding'
import { actions as GWS_ACTIONS } from '../../engage/integrations/gws/actions'
import { GWS, MODAL_NAMES } from '../../engage/utils'
import useCustomerLicenses from '../../../../hooks/useCustomerLicenses'
import useGwsEngageActionCatalog from './useGwsEngageActionCatalog'

const GWS_ACTIONS_USING_CATALOG = {
  [GWS_ACTIONS.GWS_MANAGE_GROUPS]: true,
  [GWS_ACTIONS.GWS_MANAGE_LICENSE]: true,
}

function EmployeeActionsModals(props) {
  const {
    action,
    modal,
    closeModal,
    employee,
    selectedCustomer,
    allEmployeesGroups,
    allEmployees = [],
    selectedIntegration,
    onUpdate,
  } = props

  const customerId = selectedCustomer?.id
  const { isFetchingCustomerLicenses, customerLicenses, syncCustomerLicenses, isSyncingCustomerLicenses } =
    useCustomerLicenses({ customerId, autoFetch: true })

  const handleUpdate = useCallback(() => {
    // Refresh employee data in parent component and show success toast
    if (onUpdate) {
      onUpdate(true)
    }
    closeModal()
  }, [onUpdate, closeModal])

  const handleTemporaryAccessPassUpdate = useCallback(() => {
    // Refresh employee data without closing the modal, so the user can read the generated TAP code
    if (onUpdate) {
      onUpdate(true)
    }
  }, [onUpdate])

  const currentEmployee = employee
  const employeeKey = currentEmployee?.employeeId || currentEmployee?.id || 'employee'
  const isGws = selectedIntegration?.IntegrationApplication?.application_name === GWS
  const isGwsActionCatalogEnabled = Boolean(
    isGws && modal?.openedModalName === MODAL_NAMES.INTEGRATION_MANAGER && GWS_ACTIONS_USING_CATALOG[action],
  )
  const { employees: gwsCatalogEmployees, groups: gwsCatalogGroups } = useGwsEngageActionCatalog({
    enabled: isGwsActionCatalogEnabled,
    selectedCustomer,
    selectedIntegration,
    currentEmployee,
  })

  const selectedEmployees = useMemo(() => [currentEmployee], [currentEmployee])
  const employeesList = useMemo(
    () => (allEmployees.length > 0 ? allEmployees : [currentEmployee]),
    [allEmployees, currentEmployee],
  )
  const allLicensedEmployees = useMemo(
    () => (isGws ? gwsCatalogEmployees : [currentEmployee]),
    [isGws, gwsCatalogEmployees, currentEmployee],
  )
  const groupsForManager = useMemo(
    () => (isGws ? gwsCatalogGroups : allEmployeesGroups),
    [isGws, gwsCatalogGroups, allEmployeesGroups],
  )

  return (
    <>
      {modal.openedModalName === MODAL_NAMES.INTEGRATION_MANAGER && (
        <IntegrationManager
          key={`integration-manager-${employeeKey}-${action}`}
          show
          action={action}
          onCancel={closeModal}
          selectedEmployee={currentEmployee}
          selectedEmployees={selectedEmployees}
          allAugmenttEmployees={employeesList}
          allLicensedEmployees={allLicensedEmployees}
          onUpdate={handleUpdate}
          employees={employeesList}
          allEmployeesGroups={groupsForManager}
          selectedIntegration={selectedIntegration}
        />
      )}
      {modal.openedModalName === MODAL_NAMES.MANAGE_LICENSES && (
        <ManageLicensesModal
          key={`manage-licenses-${employeeKey}`}
          show
          onHide={closeModal}
          onUpdate={handleUpdate}
          customer={selectedCustomer}
          employee={currentEmployee}
          isFetchingCustomerLicenses={isFetchingCustomerLicenses}
          customerLicenses={customerLicenses}
          syncCustomerLicenses={syncCustomerLicenses}
          isSyncingCustomerLicenses={isSyncingCustomerLicenses}
        />
      )}
      {modal.openedModalName === MODAL_NAMES.OFFBOARD_USER && (
        <OffboardModal
          show
          onHide={closeModal}
          employee={currentEmployee}
          allEmployees={employeesList}
          onUpdate={handleUpdate}
        />
      )}
      {modal.openedModalName === MODAL_NAMES.RE_REGISTER_MFA && (
        <ReRegisterMfaModal show onHide={closeModal} onUpdate={handleUpdate} employees={selectedEmployees} />
      )}
      {modal.openedModalName === MODAL_NAMES.TEMPORARY_ACCESS_PASS && (
        <TemporaryAccessPassModal
          show
          onHide={closeModal}
          onUpdate={handleTemporaryAccessPassUpdate}
          employees={selectedEmployees}
        />
      )}
      {modal.openedModalName === MODAL_NAMES.OUT_OF_OFFICE && (
        <OutOfOfficeModal show onHide={closeModal} onUpdate={handleUpdate} employees={selectedEmployees} />
      )}
      {modal.openedModalName === MODAL_NAMES.GLOBAL_ADDRESS_LIST && (
        <GlobalAddressListModal show onHide={closeModal} onUpdate={handleUpdate} employees={selectedEmployees} />
      )}
      {modal.openedModalName === MODAL_NAMES.MANAGE_EMAIL_FORWARDING && (
        <ManageEmailForwarding
          show
          onHide={closeModal}
          onUpdate={handleUpdate}
          employees={selectedEmployees}
          allEmployees={employeesList}
        />
      )}
    </>
  )
}

export default EmployeeActionsModals
